import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Zap, Globe, Search, Calendar,
  Shield, Smartphone, Clock, Star, ChevronRight, Play,
  TrendingUp, Users, Award, BarChart2
} from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { SEOHead, LOCAL_BUSINESS_SCHEMA, WEBSITE_SCHEMA } from "@/components/SEOHead";

const STATS = [
  { value: "48h", label: "Bis zur Live-Website" },
  { value: "100+", label: "Zufriedene Kunden" },
  { value: "3", label: "Pakete auf Anfrage" },
  { value: "98%", label: "Weiterempfehlungsrate" },
];

const STEPS = [
  {
    number: "01",
    title: "Kostenloses Gespräch",
    desc: "Wir analysieren Ihre aktuelle Situation und zeigen Ihnen innerhalb von 24 Stunden eine individuelle Demo-Website für Ihr Unternehmen.",
    icon: <Users size={22} />,
  },
  {
    number: "02",
    title: "Individuelle Demo",
    desc: "Sie sehen Ihre neue Website bevor Sie auch nur einen Cent bezahlen. Echte Inhalte, echtes Design — maßgeschneidert für Ihr Unternehmen.",
    icon: <Globe size={22} />,
  },
  {
    number: "03",
    title: "Live in 48 Stunden",
    desc: "Nach Ihrer Freigabe ist Ihre Website sofort online. Inklusive SEO-Grundoptimierung und mobilem Design — ohne technisches Vorwissen Ihrerseits.",
    icon: <Zap size={22} />,
  },
];

const FEATURES = [
  { icon: <Search size={20} />, title: "SEO-Optimiert", desc: "Gefunden werden auf Google – von Anfang an richtig aufgestellt." },
  { icon: <Smartphone size={20} />, title: "Mobile First", desc: "Perfektes Erscheinungsbild auf allen Geräten — Pflicht, keine Option." },
  { icon: <Calendar size={20} />, title: "Online-Buchung", desc: "Kunden buchen direkt auf Ihrer Website — kein Telefontagebuch mehr." },
  { icon: <Shield size={20} />, title: "DSGVO-Konform", desc: "Alle rechtlichen Anforderungen von Anfang an erfüllt. Kein Risiko." },
  { icon: <Clock size={20} />, title: "Schnelle Lieferung", desc: "Von der Anfrage zur fertigen Website in unter 48 Stunden." },
  { icon: <TrendingUp size={20} />, title: "Messbare Ergebnisse", desc: "Besucher-Statistiken und Performance-Tracking inklusive." },
];

const PACKAGES = [
  {
    name: "Starter",
    price: "39,99",
    setup: "199",
    desc: "Für Selbstständige und kleine Unternehmen die endlich online sichtbar sein wollen.",
    features: ["1-seitige professionelle Website", "Mobile-optimiertes Design", "Kontaktformular", "SEO-Grundoptimierung", "SSL-Zertifikat (HTTPS)", "Kostenloser Support im 1. Monat"],
    cta: "Starter anfragen",
    accent: "border-white/10",
    badge: null,
  },
  {
    name: "Business",
    price: "59,99",
    setup: "249",
    desc: "Für wachsende Unternehmen die mehr Reichweite und automatisierte Buchungen wollen.",
    features: ["Bis zu 5 Unterseiten", "Online-Buchungssystem", "Volle SEO-Optimierung", "Google My Business Setup", "Analytics-Integration", "Monatliche Berichte", "Priority-Support"],
    cta: "Business anfragen",
    accent: "border-cyan-400/50",
    badge: "Beliebt",
  },
  {
    name: "Premium",
    price: "99",
    setup: "499",
    desc: "Für Unternehmen die Marktführer in ihrer Region werden wollen — mit aktivem Wachstum.",
    features: ["Individuelle Website ohne Limit", "Aktives monatliches SEO", "Monatlicher Strategy-Call", "Content-Updates inklusive", "Konkurrenz-Analyse", "Performance-Garantie", "Dedizierter Ansprechpartner"],
    cta: "Premium anfragen",
    accent: "border-white/10",
    badge: null,
  },
];

const TESTIMONIALS = [
  {
    name: "Thomas K.",
    role: "Inhaber, KFZ-Werkstatt",
    text: "In 2 Tagen hatten wir eine Website die unsere alte um Welten schlägt. Seitdem rufen deutlich mehr Neukunden an.",
    stars: 5,
  },
  {
    name: "Sarah M.",
    role: "Inhaberin, Friseursalon",
    text: "Das Buchungssystem hat alles verändert. Kunden buchen jetzt selbst online und mein Telefon klingelt nicht mehr dauernd.",
    stars: 5,
  },
  {
    name: "Michael B.",
    role: "Geschäftsführer, Handwerksbetrieb",
    text: "Endlich eine Website die auch auf dem Handy gut aussieht. Unsere Anfragen sind um 40% gestiegen.",
    stars: 5,
  },
];

function AnimatedCounter({ value }) {
  return <span className="font-black text-4xl text-white">{value}</span>;
}

export default function HomePage() {
  return (
    <PublicLayout>
      <SEOHead
        description="signalSolution liefert professionelle, SEO-optimierte Websites für lokale Unternehmen — in 48 Stunden live. Online-Buchung, KI-gestützte Erstellung, faire Preise."
        canonical="/"
        schema={[LOCAL_BUSINESS_SCHEMA, WEBSITE_SCHEMA]}
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#050911] via-[#070d1a] to-[#050911]" />
        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-[100px] pointer-events-none" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(34,211,238,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.5) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-16">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400/10 border border-cyan-400/20 text-cyan-400 text-sm font-medium mb-8">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              Jetzt Demo anfragen — kostenlos & unverbindlich
            </div>

            {/* Headline */}
            <h1
              className="font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Websites die{" "}
              <span className="relative">
                <span className="text-cyan-400">Kunden</span>
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 300 8" fill="none" preserveAspectRatio="none">
                  <path d="M0 6 Q75 0 150 4 Q225 8 300 2" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6"/>
                </svg>
              </span>{" "}
              gewinnen.
            </h1>

            <p className="text-lg sm:text-xl text-white/60 mb-10 leading-relaxed max-w-2xl">
              signalSolution liefert professionelle, SEO-optimierte Websites für lokale Unternehmen — in 48 Stunden live, ohne technisches Vorwissen Ihrerseits.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Link
                to="/kontakt#demo-form"
                data-testid="hero-cta-btn"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-xl shadow-cyan-400/25 hover:shadow-cyan-400/40 text-base"
              >
                Kostenlose Demo anfragen
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/leistungen"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 text-white font-medium rounded-xl hover:bg-white/10 transition-all border border-white/10 text-base"
              >
                Pakete ansehen
                <ChevronRight size={18} />
              </Link>
            </div>

            {/* Checkmarks */}
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {["Keine versteckten Kosten", "Demo in 24h", "Monatlich kündbar"].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-white/50">
                  <CheckCircle2 size={15} className="text-cyan-400 flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/20 animate-bounce">
          <div className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center pt-1.5">
            <div className="w-1 h-2 bg-white/30 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#070d1a] border-y border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-black text-3xl sm:text-4xl text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {s.value}
                </div>
                <div className="text-sm text-white/40">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-3">So einfach geht's</p>
            <h2 className="font-black text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Von 0 auf Online in 48h
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Kein technisches Wissen nötig. Kein wochenlanger Prozess. Wir übernehmen alles.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-12 left-1/3 right-1/3 h-px bg-gradient-to-r from-cyan-400/20 via-cyan-400/40 to-cyan-400/20" />

            {STEPS.map((step, i) => (
              <div key={step.number} className="relative">
                <div className="bg-[#0A1220] border border-white/5 rounded-2xl p-8 hover:border-cyan-400/20 transition-all group">
                  {/* Step number */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-400 group-hover:bg-cyan-400/15 transition-colors">
                      {step.icon}
                    </div>
                    <span className="text-5xl font-black text-white/5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {step.number}
                    </span>
                  </div>
                  <h3 className="font-bold text-xl text-white mb-3">{step.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:flex absolute top-12 -right-4 z-10 w-8 h-8 rounded-full bg-[#070d1a] border border-white/10 items-center justify-center">
                    <ChevronRight size={14} className="text-cyan-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6 bg-[#070d1a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-3">Warum signalSolution?</p>
            <h2 className="font-black text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Alles inklusive. Kein Stress.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-[#0A1220] border border-white/5 rounded-xl p-6 hover:border-white/10 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-cyan-400/10 flex items-center justify-center text-cyan-400 mb-4 group-hover:bg-cyan-400/15 transition-colors">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-white/45 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview – ausgeblendet, sobald Referenzen verfügbar wieder einblenden */}
      {false && (
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-3">Unsere Arbeit</p>
              <h2 className="font-black text-4xl sm:text-5xl text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Beispiel-Websites
              </h2>
            </div>
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors whitespace-nowrap"
            >
              Alle ansehen <ArrowRight size={15} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { industry: "Physiotherapie", name: "PhysioPlus Neustadt", tag: "Business-Paket", color: "from-blue-600/20 to-cyan-600/10" },
              { industry: "Friseur", name: "Salon Éclat", tag: "Business-Paket", color: "from-purple-600/20 to-pink-600/10" },
              { industry: "Handwerk", name: "Müller Bau GmbH", tag: "Premium-Paket", color: "from-orange-600/20 to-amber-600/10" },
            ].map((item) => (
              <div key={item.name} className="group cursor-pointer">
                <div className={`aspect-[4/3] rounded-2xl bg-gradient-to-br ${item.color} border border-white/5 relative overflow-hidden mb-4 group-hover:border-white/15 transition-all`}>
                  {/* Simulated browser chrome */}
                  <div className="absolute top-0 left-0 right-0 h-8 bg-black/40 flex items-center px-3 gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                    <div className="ml-2 flex-1 h-4 bg-white/5 rounded-sm text-[8px] text-white/20 flex items-center px-2">
                      {item.name.toLowerCase().replace(/ /g, "")}.de
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center mt-8">
                    <div className="text-center px-4">
                      <div className="text-2xl font-black text-white/80 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {item.name}
                      </div>
                      <div className="text-xs text-white/30">{item.industry}</div>
                    </div>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-cyan-400/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white text-sm">{item.name}</p>
                    <p className="text-xs text-white/40">{item.industry}</p>
                  </div>
                  <span className="text-xs bg-cyan-400/10 text-cyan-400 px-3 py-1 rounded-full border border-cyan-400/20">
                    {item.tag}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Pricing Preview */}
      <section className="py-24 px-6 bg-[#070d1a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-3">Unsere Pakete</p>
            <h2 className="font-black text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Das richtige Paket für Sie
            </h2>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Klare Pakete · Individuelles Angebot · Monatlich kündbar
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {PACKAGES.map((pkg) => (
              <div
                key={pkg.name}
                className={`relative bg-[#0A1220] border-2 ${pkg.accent} rounded-2xl p-8 flex flex-col ${pkg.badge ? "shadow-xl shadow-cyan-400/10" : ""}`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-cyan-400 text-slate-900 text-xs font-bold rounded-full shadow-lg shadow-cyan-400/30">
                    {pkg.badge}
                  </div>
                )}
                <div className="mb-6">
                  <p className="text-white/40 text-sm font-medium mb-1">{pkg.name}</p>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-black text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      Auf Anfrage
                    </span>
                  </div>
                  <p className="text-xs text-white/30">Individuelles Angebot in 24 Std.</p>
                </div>

                <p className="text-sm text-white/50 mb-6 leading-relaxed">{pkg.desc}</p>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                      <CheckCircle2 size={15} className={`flex-shrink-0 mt-0.5 ${pkg.badge ? "text-cyan-400" : "text-green-400/80"}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/kontakt?paket=${pkg.name.toLowerCase()}#demo-form`}
                  className={`w-full py-3 rounded-xl font-semibold text-sm text-center transition-all ${
                    pkg.badge
                      ? "bg-cyan-400 text-slate-900 hover:bg-cyan-300 shadow-lg shadow-cyan-400/20"
                      : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                  }`}
                >
                  {pkg.cta}
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/leistungen" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
              Alle Funktionen im Detail vergleichen <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-3">Kundenstimmen</p>
            <h2 className="font-black text-4xl sm:text-5xl text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Was unsere Kunden sagen
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-[#0A1220] border border-white/5 rounded-2xl p-8 hover:border-white/10 transition-all">
                <div className="flex gap-0.5 mb-5">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-white/75 text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
                <div>
                  <p className="font-semibold text-white text-sm">{t.name}</p>
                  <p className="text-xs text-white/35 mt-0.5">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#070d1a]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative">
            <div className="absolute inset-0 -m-12 bg-cyan-500/5 rounded-3xl blur-[80px]" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center mx-auto mb-6">
                <Zap size={26} className="text-cyan-400" />
              </div>
              <h2 className="font-black text-4xl sm:text-5xl text-white mb-5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Bereit für Ihre neue Website?
              </h2>
              <p className="text-lg text-white/50 mb-8 leading-relaxed">
                Wir erstellen Ihnen kostenlos eine Demo-Website — in 24 Stunden. Unverbindlich. Ohne Risiko.
              </p>
              <Link
                to="/kontakt#demo-form"
                data-testid="bottom-cta-btn"
                className="inline-flex items-center gap-3 px-10 py-4 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-xl shadow-cyan-400/25 hover:shadow-cyan-400/40 text-base"
              >
                Jetzt kostenlos Demo anfragen
                <ArrowRight size={18} />
              </Link>
              <p className="text-sm text-white/25 mt-5">
                Keine Kreditkarte erforderlich · Kostenlos & unverbindlich
              </p>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
